import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Dumbbell, Apple, Stethoscope, Phone, Mail, Star, ArrowLeft } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'all', label: 'Todos', icon: Star, color: '#6366F1' },
  { key: 'entrenador', label: 'Entrenadores', icon: Dumbbell, color: '#EF4444' },
  { key: 'nutricionista', label: 'Nutricionistas', icon: Apple, color: '#22C55E' },
  { key: 'doctor', label: 'Doctores', icon: Stethoscope, color: '#3B82F6' },
];

const getCategoryMeta = (cat) => {
  const map = {
    entrenador: { icon: Dumbbell, color: '#EF4444', label: 'Entrenador' },
    nutricionista: { icon: Apple, color: '#22C55E', label: 'Nutricionista' },
    doctor: { icon: Stethoscope, color: '#3B82F6', label: 'Doctor' },
  };
  return map[cat] || { icon: Star, color: '#6366F1', label: cat };
};

const ClientSpecialistsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/specialists`);
        setSpecialists(res.data);
      } catch (error) { console.error('Failed to load specialists:', error); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const filtered = specialists.filter(s => {
    const matchCat = activeCategory === 'all' || s.category === activeCategory;
    const matchSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase())
      || (s.specialty || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="client-specialists-page">
      <button onClick={() => navigate('/client/services')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-services">
        <ArrowLeft size={16} /> Servicios
      </button>

      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Especialistas
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Profesionales de salud y bienestar</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          placeholder="Buscar especialista..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-11 bg-white border-gray-100 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 rounded-xl h-11 shadow-sm"
          data-testid="search-specialists"
        />
      </div>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="category-filters">
        {CATEGORIES.map((cat) => {
          const CatIcon = cat.icon;
          const isActive = activeCategory === cat.key;
          return (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-300 ${
                isActive
                  ? 'text-white shadow-lg'
                  : 'bg-white border border-gray-100 text-gray-500 hover:border-gray-200'
              }`}
              style={isActive ? { background: cat.color, boxShadow: `0 4px 14px ${cat.color}40` } : {}}
              data-testid={`filter-${cat.key}`}
            >
              <CatIcon size={14} />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Specialists list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-specialists">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Search size={32} className="text-indigo-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Sin especialistas
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {searchTerm ? 'No hay resultados para tu busqueda' : 'Aun no hay especialistas registrados en esta categoria'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((spec) => {
            const meta = getCategoryMeta(spec.category);
            const CatIcon = meta.icon;
            return (
              <div
                key={spec.id}
                className="bg-white rounded-2xl border border-gray-100/50 p-4 shadow-sm hover:shadow-md transition-all duration-300"
                data-testid={`specialist-${spec.id}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${meta.color}15` }}
                  >
                    <CatIcon size={22} style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-gray-900 text-sm truncate">{spec.name}</p>
                    </div>
                    <span
                      className="inline-block text-[10px] font-bold uppercase tracking-wide mt-0.5 px-2 py-0.5 rounded-full"
                      style={{ background: `${meta.color}15`, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                    {spec.specialty && (
                      <p className="text-xs text-gray-500 mt-1">{spec.specialty}</p>
                    )}
                    {spec.description && (
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{spec.description}</p>
                    )}
                    <div className="flex gap-2 mt-2.5">
                      {spec.phone && (
                        <a href={`tel:${spec.phone}`} className="flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1.5 hover:bg-gray-100 transition-colors" data-testid={`spec-phone-${spec.id}`}>
                          <Phone size={11} /> {spec.phone}
                        </a>
                      )}
                      {spec.email && (
                        <a href={`mailto:${spec.email}`} className="flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1.5 hover:bg-gray-100 transition-colors" data-testid={`spec-email-${spec.id}`}>
                          <Mail size={11} /> Correo
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientSpecialistsPage;
